/* eslint-disable @typescript-eslint/camelcase */
import { ClientMapper } from "@/data/persistences/mappers/ClientMapper";
import { ClientRepositoryInterface } from "@/data/persistences/repositories/contracts/ClientRepositoryInterface";
import {
  Actor,
  ClientDetail,
  ClientEntities,
  ClientMappingList,
  ClientParentData,
  ClientPartnerData,
  RequestApiClientMappingList
} from "@/domain/entities/Client";
import { Endpoints } from "../../misc/Endpoints";
import ApiService from "../../services/ApiService";
import { EditClientManagementRequestInterface } from "@/data/payload/contracts/ClientManagementRequest";
import { ResponsePayload } from "@/domain/entities/ResponsePayload";
import { ApiRequestList } from "@/domain/entities/MainApp";
import { Pagination } from "@/domain/entities/Pagination";
import { ReportListDownload, ReportListDownloadData } from "@/domain/entities/Report";
import parsingLinkGcpToAws from "@/app/infrastructures/misc/common-library/ParsingLinkGcpToAws";
import { getToken } from "@/app/infrastructures/misc/Cookies";

export class ClientApiRepository implements ClientRepositoryInterface {
  private service: ApiService;
  private mapper: ClientMapper;
  private endpoints: Endpoints;

  constructor(service: ApiService, mapper: ClientMapper, endpoints: Endpoints) {
    this.service = service;
    this.mapper = mapper;
    this.endpoints = endpoints;
  }

  public async getList(
    page: number,
    limit: number,
    search: string,
    status: string,
    clientParentId?: number
  ): Promise<ClientEntities> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getClient(page, limit, search, status, clientParentId),
      {}
    );
    return this.mapper.convertClientFromApi(resp);
  }

  public async getListClientPartner(
    page: number,
    limit: number,
    search: string,
    partnerType: string,
    type?: string,
    isBranch?: boolean,
    isParent?: boolean,
    orderBy?: string,
    sortBy?: string,
    countryCode?: string
  ): Promise<ClientPartnerData[]> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getListClientPartner(
        page,
        limit,
        search,
        partnerType,
        type,
        isBranch,
        isParent,
        orderBy,
        sortBy,
        countryCode
      ),
      {}
    );
    return this.mapper.convertClientPartnerFromApi(resp);
  }

  public async getClientParentList(): Promise<ClientParentData[]> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getListClientParent(),
      {}
    );
    return this.mapper.convertClientParentDataFromApi(resp);
  }

  public async getClientMappingList(
    params: RequestApiClientMappingList
  ): Promise<ClientMappingList[]> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getListClientMapping(params),
      {}
    );
    return this.mapper.convertClientMappingFromApi(resp);
  }

  public async getListOfActor(search: string): Promise<Actor[]> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getListOfActors(search),
      {}
    );
    return this.mapper.convertActorsFromApi(resp);
  }

  public async retryUploadClientRate(
    payload: EditClientManagementRequestInterface
  ): Promise<ResponsePayload> {
    const response = await this.service.invoke(
      "post",
      this.endpoints.retryUploadClientRate(),
      undefined,
      payload
    );
    return this.mapper.convertChangeDataFromApi(response);
  }
  public async getClientDetail(clientId: number): Promise<ClientDetail> {
    const resp = await this.service.invoke(
      "GET",
      `${this.endpoints.editClientManagemetBasic()}/detail/${clientId}`
    );

    return this.mapper.convertClientDetailFromApi(resp);
  }

  public async getRTCListClientParent(
    hubId: number,
    transportType: string
  ): Promise<ClientParentData[]> {
    const resp = await this.service.invoke(
      "GET",
      this.endpoints.getRTCListClientParent(hubId, transportType)
    );
    return this.mapper.convertClientParentDataFromApiHydra(resp);
  }

  public async getListIntegrationToken(params: ApiRequestList) {
    const resp = await this.service.invoke(
      "GET",
      this.endpoints.getListIntegrationToken(params)
    );
    return this.mapper.convertClientParentDataFromApiHydra(resp);
  }

  public async getClientRateBulkDownloadList(params: {
    page: number;
    limit: number;
    type: string;
    clientId: number;
    isTotalData: boolean;
  }): Promise<ReportListDownload> {
    const resp = await this.service.invoke(
      "GET",
      this.endpoints.getClientRateBulkDownloadList(),
      {
        page: params.page,
        limit: params.limit,
        type: params.type,
        client_id: params.clientId,
        is_total_data: params.isTotalData
      }
    );

    const { data } = resp;
    const list: ReportListDownloadData[] = [];
    data.data?.map((item: any) =>
      list.push(
        new ReportListDownloadData({
          id: item.id,
          url: parsingLinkGcpToAws(item.url),
          type: item.type,
          createdAt: item.created_at,
          status: item.status,
          isRequireToken: item.is_require_token,
          filename: item.file_name
        })
      )
    );

    return new ReportListDownload(
      new Pagination(data.meta.page, data.meta.limit),
      list
    );
  }

  public async getBulkErrorReport(id: number): Promise<any> {
    const resp = await this.service.client.get(
      this.endpoints.bulkErrorReport(id),
      {
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`
        }
    });
    return this.mapper.convertExportReportBulkError(resp);
  }
}
